import React, { useState, useEffect } from 'react';
import { Box, Flex, Grid, Heading, Text, Button, VStack, Checkbox, Image } from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import TermsModal from './TermsModal';
import GoogleLoginButton from './GoogleLogin';
import AppSkeleton from './AppSkeleton';
import line from '../Images/Line.svg';
import { completeLogin, signInWithGoogle } from './auth/completeLogin';
import { pendingJoin } from './auth/pendingJoin';
import { BASE_API_URL } from './config';
import { a, useAppColorMode } from './appColors';
import { getRecaptchaToken, preloadRecaptcha, recaptchaEnabled } from './recaptcha';
import {
  ROLES,
  useRoleParam,
  primaryButtonStyle,
  AuthField,
  RevealButton,
  RoleChooser,
  ChangeRole,
  AccentLink,
  AuthNotice,
  AuthPage,
  RecaptchaNotice,
} from './auth/AuthShell';

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

const INTRO = {
  student: "Solve past papers, get marked and track your progress — free.",
  instructor: "Set up your spaces, set work and review your students' answers.",
};

const CreateAccount = () => {
  const { isDark, toggle } = useAppColorMode();
  const [role, chooseRole] = useRoleParam();
  const [showPassword, setShowPassword] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The address the confirmation went to, once the account exists.
  const [createdFor, setCreatedFor] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // Google found an account of the other kind for this address.
  const [wrongRole, setWrongRole] = useState(null);
  const [isGoogleBusy, setIsGoogleBusy] = useState(false);
  const navigate = useNavigate();

  // Fetch the captcha script early so the first submit isn't held up by it.
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // A new choice starts clean.
  useEffect(() => {
    setSubmitError(null);
    setCreatedFor(null);
    setWrongRole(null);
  }, [role]);

  const isStudent = role === "student";

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      // An email invite was sent to one address: start them on it.
      email: pendingJoin()?.email || "",
      dob: "",
      password: "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      // Asked of students only; an instructor's age isn't ours to need.
      dob: isStudent ? Yup.date().required("Date of birth is required") : Yup.mixed().notRequired(),
      password: Yup.string()
        .required("Password is required")
        .matches(
          PASSWORD_RULE,
          "Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol"
        ),
    }),
    onSubmit: async (values) => {
      setSubmitError(null);
      if (!hasAgreed) {
        setIsModalOpen(true);
        return;
      }

      // Mint a captcha token first. If it's switched on but can't run (script
      // blocked, offline), stop with something actionable.
      const recaptchaToken = await getRecaptchaToken("register");
      if (recaptchaEnabled && !recaptchaToken) {
        setSubmitError("We couldn't run the security check. Disable any ad blocker and try again.");
        return;
      }

      const requestBody = {
        first_name: values.firstname,
        last_name: values.lastname,
        email: values.email,
        dob: isStudent && values.dob ? new Date(values.dob).toISOString() : null,
        password: values.password,
        user_type: ROLES[role].userType,
        recaptcha_token: recaptchaToken,
        // From a class email invite: signing up with the invited address skips
        // the confirmation email and signs them straight in.
        invite_token: isStudent ? pendingJoin()?.token : undefined,
      };

      try {
        const response = await fetch(`${BASE_API_URL}/api/v1/auth/register-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          if (data.access_token) {
            // Signed in already: on into the class they were invited to.
            await completeLogin({ session: data, role, navigate });
          } else {
            setCreatedFor(values.email);
          }
        } else {
          // FastAPI returns the message under `detail` (string or validation array).
          const errorData = await response.json().catch(() => ({}));
          const detail = errorData.detail;
          setSubmitError(
            typeof detail === "string" ? detail : "Please check your details and try again."
          );
        }
      } catch (error) {
        console.error("Error:", error);
        setSubmitError("Something went wrong while creating your account. Please try again.");
      }
    },
  });

  // The date of birth is re-validated when the choice changes it from
  // required to not.
  useEffect(() => {
    if (formik.submitCount > 0) formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const loginLink = (
    <>
      Already have an account?{" "}
      <Link to={role ? `/login?as=${role}` : "/login"}>
        <AccentLink>Log in</AccentLink>
      </Link>
    </>
  );

  const chooser = (
    <RoleChooser
      heading="Create your Stande account"
      subheading="Who's joining?"
      onChoose={chooseRole}
      isDark={isDark}
    />
  );

  // Google both creates the account (as the role chosen here) and signs in,
  // so the terms are agreed first, as with the form.
  const canStartGoogle = () => {
    if (hasAgreed) return true;
    setIsModalOpen(true);
    return false;
  };

  const handleGoogleSuccess = async (response) => {
    setIsGoogleBusy(true);
    setSubmitError(null);
    setWrongRole(null);
    try {
      const session = await signInWithGoogle(response.code, role);
      const { wrongRole: actual } = await completeLogin({ session, role, navigate });
      if (actual) setWrongRole(actual);
    } catch (error) {
      console.error("Google sign-up error:", error);
      setSubmitError("Google sign-up failed. Please try again.");
    } finally {
      setIsGoogleBusy(false);
    }
  };

  // As SolvePaper confirms a submitted paper: a blue tick, a heading, and the
  // one thing to do next.
  const created = createdFor && (
    <Flex direction="column" align="center" textAlign="center" gap="6px">
      <Flex align="center" justify="center" boxSize="72px" borderRadius="full" bg={a.blue} mb="18px">
        <Text fontSize="34px" lineHeight="1" color="#000000">✓</Text>
      </Flex>
      <Text fontSize="24px" fontWeight="600" color={a.text}>
        Check your email
      </Text>
      <Text fontSize="15px" fontWeight="300" color={a.textSub} maxW="380px">
        We've sent a confirmation link to{" "}
        <Text as="span" fontWeight="500" color={a.text}>{createdFor}</Text>.
        Open it to finish setting up your {ROLES[role].title.toLowerCase()} account, then log in
        {pendingJoin() ? ` and you'll be added to ${pendingJoin().courseName}.` : "."}
      </Text>
      <Link to={`/login?as=${role}`} style={{ width: "100%" }}>
        <Button sx={primaryButtonStyle} mt="22px" display="inline-flex">
          Go to log in
        </Button>
      </Link>
      <Text fontSize="12px" color={a.textMuted} mt="10px">
        Can't find it? Check your spam folder.
      </Text>
    </Flex>
  );

  const joining = pendingJoin();

  const form = role && (
    <>
      <ChangeRole role={role} onClick={() => chooseRole(null)} />
      {joining && role === "student" && (
        <Box mb="16px">
          <AuthNotice tone="warn" title={`Sign up to join ${joining.courseName}`}>
            Confirm your email, then log in, and you'll be added to the space.
          </AuthNotice>
        </Box>
      )}
      <Heading mb="4px" color={a.text} fontSize="24px" fontWeight="600">
        Create {ROLES[role].article} account
      </Heading>
      <Text color={a.textSub} fontSize="15px" fontWeight="300" mb={{ base: 5, md: 8 }}>
        {INTRO[role]}
      </Text>

      <form onSubmit={formik.handleSubmit} noValidate>
        <VStack alignItems="stretch" spacing={{ base: 4, md: 5 }}>
          <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={{ base: 4, md: "14px" }}>
            <AuthField formik={formik} name="firstname" label="First name" autoComplete="given-name" />
            <AuthField formik={formik} name="lastname" label="Last name" autoComplete="family-name" />
          </Grid>
          <AuthField formik={formik} name="email" label="Email" type="email" autoComplete="email" />
          {isStudent && (
            <AuthField formik={formik} name="dob" label="Date of birth" type="date" autoComplete="bday" />
          )}
          <AuthField
            formik={formik}
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            hint="At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol."
            rightElement={<RevealButton shown={showPassword} onToggle={() => setShowPassword((s) => !s)} />}
          />

          <Flex align="center" gap="10px" textAlign="left">
            <Checkbox
              isChecked={hasAgreed}
              // Agreeing happens in the modal, after reading; the box opens it.
              onChange={() => (hasAgreed ? setHasAgreed(false) : setIsModalOpen(true))}
              sx={{
                ".chakra-checkbox__control": {
                  borderColor: a.inputBorder,
                  borderRadius: "5px",
                  bg: a.inputBg,
                },
                ".chakra-checkbox__control[data-checked]": {
                  bg: a.accent,
                  borderColor: a.accent,
                  color: a.onAccent,
                },
              }}
            />
            <Text fontSize="14px" fontWeight="400" color={a.text2}>
              I agree to the{" "}
              <Button
                variant="unstyled"
                onClick={() => setIsModalOpen(true)}
                h="auto"
                minW={0}
                fontSize="14px"
                fontWeight="500"
                color={a.accent}
                verticalAlign="baseline"
                _hover={{ textDecoration: "underline" }}
              >
                Terms and Conditions
              </Button>
            </Text>
          </Flex>
        </VStack>

        {submitError && (
          <Box mt={{ base: 4, md: 6 }}>
            <AuthNotice>{submitError}</AuthNotice>
          </Box>
        )}

        {wrongRole && (
          <Box mt={{ base: 4, md: 6 }}>
            <AuthNotice tone="warn" title={`You already have ${ROLES[wrongRole].article} account`}>
              That Google account is already set up as {ROLES[wrongRole].article}.{" "}
              <Link to={`/login?as=${wrongRole}`}>
                <AccentLink>Log in as {ROLES[wrongRole].article}</AccentLink>
              </Link>
            </AuthNotice>
          </Box>
        )}

        <Button
          type="submit"
          sx={primaryButtonStyle}
          mt={{ base: 5, md: 8 }}
          display="inline-flex"
          isLoading={formik.isSubmitting}
          loadingText="Creating account"
        >
          Create account
        </Button>
        <RecaptchaNotice />
      </form>

      <Flex align="center" justifyContent="center" mt={{ base: 5, md: 7 }} mb={{ base: 5, md: 6 }}>
        <Image src={line} alt="" />
        <Text color={a.textSub} fontSize="14px" mx={4} whiteSpace="nowrap">
          or
        </Text>
        <Image src={line} alt="" />
      </Flex>

      <GoogleLoginButton
        label="Sign up with Google"
        canStart={canStartGoogle}
        onSuccess={handleGoogleSuccess}
        onError={() => setSubmitError("Google sign-up failed. Please try again.")}
      />

      <Text color={a.textSub} fontSize="14px" fontWeight="300" mt={{ base: 5, md: 6 }} textAlign="center">
        {loginLink}
      </Text>
    </>
  );

  return (
    <AuthPage isDark={isDark} toggle={toggle} wide={!role} centered={!role || !!createdFor}>
      {isGoogleBusy && <AppSkeleton />}
      {created || form || chooser}
      <TermsModal
        isOpen={isModalOpen}
        isDark={isDark}
        onClose={() => setIsModalOpen(false)}
        onAgree={() => setHasAgreed(true)}
      />
    </AuthPage>
  );
};

export default CreateAccount;
